import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { IconButton } from '@cbhq/cds-web/buttons/IconButton';
import { ListCell } from '@cbhq/cds-web/cells/ListCell';
import { HStack } from '@cbhq/cds-web/layout';
import { Text } from '@cbhq/cds-web/typography/Text';

import { logError } from '../../../utils/logging';

const LIST_CELL_STYLES = {
  end: { overflow: 'visible' },
} as const;

export type AuditLogDetailRowProps = {
  label: ReactNode;
  value: ReactNode;
  copyValue?: string;
  copyAccessibilityLabel?: string;
  onCopySuccess?: () => void;
};

export function AuditLogDetailRow({
  label,
  value,
  copyValue,
  copyAccessibilityLabel,
  onCopySuccess,
}: AuditLogDetailRowProps) {
  const handleCopy = useCallback(() => {
    if (!copyValue) {
      return;
    }

    navigator.clipboard
      .writeText(copyValue)
      .then(() => {
        onCopySuccess?.();
      })
      .catch((err) => {
        logError(err, {
          context: 'AuditLogDetailRow.handleCopy',
          isHandled: true,
        });
      });
  }, [copyValue, onCopySuccess]);

  return (
    <ListCell
      spacingVariant="condensed"
      priority="end"
      styles={LIST_CELL_STYLES}
      title={label}
      end={
        copyValue ? (
          <HStack alignItems="center" gap={0.75} paddingStart={2} minWidth={0}>
            <Text as="span" font="body" color="fg" textAlign="end" overflow="truncate">
              {value}
            </Text>
            <IconButton
              transparent
              color="fg"
              compact
              name="copy"
              onClick={handleCopy}
              accessibilityLabel={copyAccessibilityLabel ?? String(label)}
            />
          </HStack>
        ) : (
          <Text as="span" font="body" color="fg" textAlign="end" overflow="truncate">
            {value}
          </Text>
        )
      }
      testID="audit-log-detail-row"
    />
  );
}
